import { memo, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { usePopper } from "react-popper";
import { Props } from "react-select";
// eslint-disable-next-line import/no-unresolved
import { MenuPortalProps } from "react-select/dist/declarations/src/components/Menu";

import stylesCss from "./style.module.scss";

type PortalProps = MenuPortalProps<any, any, any> & {
  selectProps: Props<any, any, any> & { customMenuShouldBlockScroll?: boolean };
};

export const MenuPortal = memo<PortalProps>((props) => {
  const { appendTo, children, className, controlElement, menuPosition, getStyles, selectProps } = props;

  const [ popperElement, setPopperElement ] = useState<HTMLDivElement | null>(null);
  const { styles, attributes, update } = usePopper(controlElement, popperElement, {
    placement: "bottom-start",
    modifiers: [
      {
        name: "flip",
        options: {
          fallbackPlacements: [ "bottom-end", "top-start", "top-end", "right", "left" ],
        },
      },
    ],
  });

  useEffect(() => {
    update?.();
  }, [ update, children ]);

  const parentNode = document.querySelector("#popperPortal");

  const menuContainsEventTarget = useCallback((e: WheelEvent | MouseEvent) => {
    if (e.target) {
      if (popperElement?.contains(e.target as Node)) {
        //hotfix for inner select components (like inner select)
        if (parentNode?.contains(e.target as Node)) {
          return true;
        }
      }
    }
    return false;
  }, [ popperElement, parentNode ]);

  useEffect(() => {
    if (selectProps.customMenuShouldBlockScroll) {
      const wheelListener = (e: WheelEvent) => {
        if (!menuContainsEventTarget(e)) e.preventDefault();
      };

      const mouseMoveListener = (e: MouseEvent) => {
        if (!menuContainsEventTarget(e)) e.stopImmediatePropagation();
      };

      document.addEventListener("mousemove", mouseMoveListener);
      document.addEventListener("wheel", wheelListener, { passive: false });
      return () => {
        document.removeEventListener("wheel", wheelListener);
        document.removeEventListener("mousemove", mouseMoveListener);
      };
    }
  }, [ selectProps.customMenuShouldBlockScroll, menuContainsEventTarget ]);

  if (!appendTo || !controlElement) {
    return null;
  }

  return (
    createPortal(
      <div
        className={classNames(stylesCss.menuPortal, className)}
        ref={setPopperElement}
        style={{
          ...getStyles(
            "menuPortal",
            { offset: -1, position: menuPosition, rect: controlElement?.getBoundingClientRect()! },
          ) as any,
          ...styles.popper,
        }}
        {...attributes.popper}
      >
        {children}
      </div>,
      parentNode!,
    )
  );
});
